import {
    Box,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useMemo } from "react";
import SwitchToggle from "../../components/SwitchToggle";
import { useGetRoles, useDeleteRole } from "../../api/hooks/usePermission";
import { showError, showSuccess } from "../../helpers/toast";
import type { Role } from "../../api/types/permission.types";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import { useState } from "react";
import Pagination from "../../components/Panigation";

import { Dispatch, SetStateAction } from "react";

interface RolesListProps {
    searchTerm: string;
    page: number;
    limit: number;
    onPageChange: (newPage: number) => void;
    setPage: Dispatch<SetStateAction<number>>;
}

const RolesList: React.FC<RolesListProps> = ({ searchTerm, page, limit, onPageChange, setPage }) => {
    const navigate = useNavigate();
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [roleToDelete, setRoleToDelete] = useState<string | null>(null);

    // Fetch roles
    const { data: rolesData, isLoading: isLoadingRoles, refetch: refetchRoles, error: rolesError } = useGetRoles(
        {
            ...(searchTerm.trim() && { search: searchTerm.trim() }),
            page,
            limit,
            offset: (page - 1) * limit,
        },
        { enabled: true }
    );

    // Extract roles and totalCount from response
    const { roles, totalCount } = useMemo(() => {
        const data = rolesData?.data;
        let rolesList: Role[] = [];
        let total = 0;

        if (Array.isArray(data)) {
            rolesList = data;
            total = data.length;
        } else if (data && typeof data === 'object') {
            const dataObj = data as any;
            if ('list' in dataObj && Array.isArray(dataObj.list)) {
                rolesList = dataObj.list;
                total = dataObj.totalCount || dataObj.list.length;
            } else if ('data' in dataObj && Array.isArray(dataObj.data)) {
                rolesList = dataObj.data;
                total = dataObj.totalCount || dataObj.data.length;
            } else if (dataObj.totalCount !== undefined) {
                total = dataObj.totalCount;
            }
        }

        if (!Array.isArray(rolesList)) {
            rolesList = [];
        }

        return {
            roles: rolesList,
            totalCount: total || rolesList.length,
        };
    }, [rolesData]);

    // Delete role mutation
    const { mutate: deleteRole, isPending: isDeleting } = useDeleteRole({
        onSuccess: (response) => {
            showSuccess(response.message || "Role deleted successfully");
            refetchRoles();
            setDeleteDialogOpen(false);
            setRoleToDelete(null);
        },
        onError: (error: any) => {
            showError(error?.response?.data?.message || "Failed to delete role");
        },
    });

    const formatDate = (dateString?: string) => {
        if (!dateString) return "N/A";
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
            });
        } catch {
            return "N/A";
        }
    };

    const handleDeleteRole = (roleId: string) => {
        setRoleToDelete(roleId);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = () => {
        if (roleToDelete) {
            deleteRole(roleToDelete);
        }
    };

    const handleCancelDelete = () => {
        setDeleteDialogOpen(false);
        setRoleToDelete(null);
    };

    const handleViewRole = (role: Role) => {
        navigate("/manage-subadmin/roles/detail", { state: { roleId: role._id } });
    };

    const handleEditRole = (role: Role) => {
        navigate("/manage-subadmin/roles/add", { state: { roleId: role._id } });
    };

    if (isLoadingRoles) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                <CircularProgress />
            </Box>
        );
    }

    if (rolesError) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                <p>Error loading roles. Please try again.</p>
            </Box>
        );
    }

    const totalPages = Math.ceil(totalCount / limit);

    return (
        <>
            <TableContainer className="table_container">
                {roles.length === 0 ? (
                    <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                        <p>No roles found.</p>
                    </Box>
                ) : (
                    <Table>
                        <TableHead>
                            <TableRow hover>
                                <TableCell>Sr. No.</TableCell>
                                <TableCell>Role Name</TableCell>
                                <TableCell>Created Date</TableCell>
                                <TableCell>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {roles.map((role: Role, index: number) => (
                                <TableRow hover key={role._id}>
                                    <TableCell>{(page - 1) * limit + index + 1}</TableCell>
                                    <TableCell>
                                        <a
                                            href="/manage-subadmin/roles/detail"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                handleViewRole(role);
                                            }}
                                            className="user_block click_action"
                                        >
                                            <p>
                                                <b>{role.name}</b>
                                            </p>
                                        </a>
                                    </TableCell>
                                    <TableCell>{formatDate(role.createdAt)}</TableCell>
                                    <TableCell>
                                        <div className="table_actions v2">
                                            <IconButton
                                                onClick={() => handleViewRole(role)}
                                                disabled={isDeleting}
                                                title="View Role"
                                            >
                                                <img src="/static/images/eye_icon.svg" alt="view" />
                                            </IconButton>
                                            <IconButton
                                                onClick={() => handleEditRole(role)}
                                                disabled={isDeleting}
                                                title="Edit Role"
                                            >
                                                <img src="/static/images/edit2_icon.svg" alt="edit" />
                                            </IconButton>
                                            <IconButton
                                                onClick={() => handleDeleteRole(role._id)}
                                                disabled={isDeleting}
                                                title="Delete Role"
                                            >
                                                <img src="/static/images/trash2_icon.svg" alt="delete" />
                                            </IconButton>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </TableContainer>

            {/* Pagination */}
            {!isLoadingRoles && !rolesError && totalCount > 0 && (
                <Pagination
                    module={roles}
                    page={page}
                    onPageChange={onPageChange}
                    totalPages={totalPages}
                    totalCount={totalCount}
                    setPage={setPage}
                    limit={limit}
                />
            )}

            {/* Delete Confirmation Dialog */}
            <ConfirmationDialog
                open={deleteDialogOpen}
                onClose={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                title="Delete Role"
                message="Are you sure you want to delete this role? This action cannot be undone."
                confirmText="Delete"
                cancelText="Cancel"
                confirmButtonColor="error"
                isLoading={isDeleting}
                loadingText="Deleting..."
            />
        </>
    );
};

export default RolesList;
